/// <amd-module name="Feed/Legacy/Protocols/Npm/Commands/NpmDeprecateDialog" />
import * as React from "react";

import { TextField } from "OfficeFabric/TextField";

import { ProgressAnnouncer } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";

import { IButtonProps } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";

import { Package, PackageVersion } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { IError } from "Feed/Common/Types/IError";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { NpmProtocolMetadata } from "Feed/Legacy/Protocols/Npm/NpmContracts";

export interface INpmDeprecateDialogProps {
    packageSummary: Package;
    packageVersion: PackageVersion;
    onDismiss: () => void;
    deprecatePackageDelegate: (deprecateMessage: string) => Promise<any>;
}

export interface INpmDeprecateDialogState {
    hidden: boolean;
    initialDeprecateMessage?: string;
    hideUndeprecateButton: boolean;
    disableDeprecateButton: boolean;
    isSaving: boolean;
}

export class NpmDeprecateDialog extends React.Component<INpmDeprecateDialogProps, INpmDeprecateDialogState> {
    private deprecateMessage: string;

    constructor(props: INpmDeprecateDialogProps) {
        super(props);

        const protocolMetadata: NpmProtocolMetadata = props.packageVersion.protocolMetadata.data;
        const initialDeprecated: string = protocolMetadata.deprecated;

        this.state = {
            hidden: false,
            initialDeprecateMessage: initialDeprecated,
            hideUndeprecateButton: initialDeprecated == null || initialDeprecated.length === 0,
            disableDeprecateButton: true,
            isSaving: false
        };

        this.deprecateMessage = initialDeprecated ? initialDeprecated : "";
    }

    public render(): JSX.Element {
        return (
            !this.state.hidden && (
                <Dialog
                    calloutClassName="deprecate-package-dialog"
                    calloutContentClassName="deprecate-dialog"
                    defaultActiveElement=".dialog-content-textarea"
                    footerButtonProps={this._getFooterButtonProps()}
                    lightDismiss={true}
                    onDismiss={this._closeDialog}
                    titleProps={{
                        text: Utils_String.format(
                            "{0} {1} {2}",
                            PackageResources.DeprecatePackageTitle,
                            this.props.packageSummary.name,
                            this.props.packageVersion.version
                        )
                    }}
                >
                    {this._renderDescription()}
                    {this._renderMessageTextBox()}
                </Dialog>
            )
        );
    }

    private _closeDialog = (): void => {
        this.setState({ hidden: true });
        this.props.onDismiss();
    };

    private _renderDescription(): JSX.Element {
        return (
            <div>
                <div>
                    <span className="deprecate-package-info">
                        {this.state.initialDeprecateMessage
                            ? PackageResources.DeprecatePackageDialogWarning1_Deprecated
                            : PackageResources.DeprecatePackageDialogWarning1}
                    </span>
                    <span className="deprecate-package-info deprecate-package-info-bold">
                        {this.props.packageVersion.version}
                    </span>
                    <span className="deprecate-package-info">{PackageResources.DeprecatePackageDialogWarning2}</span>
                    <span className="deprecate-package-info deprecate-package-info-bold">
                        {this.props.packageSummary.name + (this.state.initialDeprecateMessage ? "" : ".")}
                    </span>
                    {this.state.initialDeprecateMessage ? (
                        <span className="deprecate-package-info">
                            {PackageResources.DeprecatePackageDialogWarning3_Deprecated}
                        </span>
                    ) : null}
                </div>
                <div className="deprecate-package-warning">{PackageResources.DeprecatePackageDialogInfo}</div>
            </div>
        );
    }

    private _getFooterButtonProps(): IButtonProps[] {
        const buttonProps: IButtonProps[] = [];
        buttonProps.push({
            ariaLabel: PackageResources.Dialog_CancelButtonText,
            className: "cancel-button",
            onClick: this._closeDialog,
            text: PackageResources.Dialog_CancelButtonText
        });

        if (!this.state.hideUndeprecateButton) {
            buttonProps.push({
                primary: true,
                className: "undeprecate-button",
                onClick: () => this._invokeDeprecate(true),
                ariaLabel: PackageResources.UndeprecatePackageTitle,
                disabled: this.state.isSaving,
                text: PackageResources.UndeprecatePackageTitle
            });
        }

        buttonProps.push({
            primary: true,
            className: "deprecate-button",
            onClick: () => this._invokeDeprecate(),
            ariaLabel: PackageResources.DeprecatePackageTitle,
            disabled: this.state.disableDeprecateButton || this.state.isSaving,
            text: this.state.isSaving
                ? PackageResources.NpmDeprecate_ProgressStarted
                : PackageResources.DeprecatePackageTitle
        });
        return buttonProps;
    }

    private _renderMessageTextBox(): JSX.Element {
        return (
            <TextField
                ariaLabel={PackageResources.DeprecatePackageDialogMessage}
                inputClassName="dialog-content-textarea"
                defaultValue={this.props.packageVersion.protocolMetadata.data.deprecated}
                label={PackageResources.DeprecatePackageDialogMessage}
                placeholder={PackageResources.DeprecatePackageDialogPlaceholderMessage}
                rows={3}
                multiline={true}
                resizable={false}
                maxLength={255}
                onChanged={this._onChanged}
                disabled={this.state.isSaving}
            />
        );
    }

    private _invokeDeprecate = (isUnDeprecate: boolean = false): void => {
        let announceStart = PackageResources.NpmDeprecate_ProgressStarted;
        let announceEnd = PackageResources.NpmDeprecate_ProgressEnded;
        let announceError = PackageResources.NpmDeprecate_ProgressFailed;

        if (isUnDeprecate) {
            this.deprecateMessage = "";

            announceStart = PackageResources.NpmDeprecate_ProgressStarted_Undeprecate;
            announceEnd = PackageResources.NpmDeprecate_ProgressEnded_Undeprecate;
            announceError = PackageResources.NpmDeprecate_ProgressFailed_Undeprecate;
        }

        // Disable buttons as we try to deprecate the package
        this.setState({
            isSaving: true
        });

        const deprecatePromise = this.props.deprecatePackageDelegate(this.deprecateMessage).then(
            success => {
                this._closeDialog();
            },
            err => {
                // The delegate method should surface the error message
                Actions.ErrorEncountered.invoke({
                    message: PackageResources.NpmDeprecate_ProgressFailed,
                    details: err
                } as IError);

                this.setState({
                    isSaving: false
                });
            }
        );

        ProgressAnnouncer.forPromise(deprecatePromise, {
            announceStartMessage: announceStart,
            announceEndMessage: announceEnd,
            announceErrorMessage: announceError
        });
    };

    private _onChanged = (text: string): void => {
        if (text) {
            this.deprecateMessage = text;
            if (text !== this.state.initialDeprecateMessage) {
                this.setState({
                    disableDeprecateButton: false
                });
            }
        } else {
            this.setState({
                disableDeprecateButton: true
            });
        }
    };
}
